import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewAdultAction,
  removeAdultAction,
  removeFiles,
  removeOtherAdultAssessmentFiles,
  setOtherAdultAssessmentFiles
} from "../../../../redux/actions";
import BackAnchor from "../../../../shared/components/BackAnchor";
import OtherAdultFormComponent from "./OtherAdultFormComponent";
import ContinueCancelButtons from "../../../../shared/components/ContinueCancelButtons";
import FileUpload from "../../../../shared/components/FileUpload";
import { bytesToSize, fileToBase64, getFileTypeFromMimeType } from "../../../../helpers/fileHelpers";
import Loader from "../../../../shared/components/Loader";
import {
  validateFullNameErrors,
  validateLastNameErrors,
  validateSINNumberErrors
} from "../../../../shared/helpers/validators";

import { getApplicantsAge } from "../../../../helpers/utils";
import { faBeer, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const MAX_OTHER_ADULTS_COUNT = 5;

const OtherAdultsFormView = ({ t, i18n, setIsError }) => {
  const dispatch = useDispatch();
  const [otherAdultsFormErrors, setOtherAdultsFormErrors] = useState({});

  const otherAdultsInformation = useSelector(
    (state) => state?.app?.otherAdultsInformation
  );

  const assessmentFiles = useSelector(
    (state) => state.app.otherAdultAssessmentFiles
  );

  const questionDataList = useSelector((state) => state.app.questionDataList);
  const userData = useSelector((state) => state.app);

  const datesData = useSelector((state) => state.datesReducer);
  let dates, currentLanguageDates;
  if (datesData) {
    dates = datesData["en"];
    currentLanguageDates = datesData[i18n.language];
  }

  const anotherAdultLiving =
    questionDataList[
      questionDataList.findIndex((question) => question.questionNumber === 7)
    ].flag;

  const navigate = useNavigate();
  const { search } = useLocation();
  const url = new URLSearchParams(search);

  // Get the value of the 'isSummary' parameter
  const isSummary = url?.get("isSummary");
  const [dobFlag, setDobFlag] = useState(false);
  const [dobValidCharacter, setDobValidCharacter] = useState("");
  const [OtherAdultDob, setOtherAdultDob] = useState([]);


  const getDate = (localId, day, month, year) => {
    setOtherAdultDob((prevDob) => {
      // Check if localId already exists in the array
      const existingDobIndex = prevDob.findIndex((x) => x.localId === localId);

      if (existingDobIndex !== -1) {
        // Update the existing entry by creating a new array with the updated entry
        const updatedDob = [...prevDob];
        updatedDob[existingDobIndex] = { localId, day, month, year };
        return updatedDob;
      } else {
        // If the localId doesn't exist, add a new entry
        return [...prevDob, { localId, day, month, year }];
      }
    });
  };

  const haveFormErrors = () => {
    const formErrors = {};
    otherAdultsInformation?.forEach((other) => {
      const otherError = {};
      let { day, month, year, localId } = OtherAdultDob.filter((x) => x.localId === other.localId)[0]
      const firstNameError = validateFullNameErrors(other.firstName);
      if (firstNameError) {
        otherError.firstName = firstNameError;
      }

      const lastNameError = validateLastNameErrors(other.lastName);
      if (lastNameError) {
        otherError.lastName = lastNameError;
      }

      let enteredSINs = userData.otherAdultsInformation.filter(x => x.localId !== other.localId).map(y => y.socialInsuranceNumber)
      const SINError = validateSINNumberErrors(other.socialInsuranceNumber, userData.personalInformation.socialInsuranceNumber, enteredSINs);
      if (SINError) {
        otherError.socialInsuranceNumber = SINError;
      }

      const dateOfBirthError = !day || !month || !year;
      if (dateOfBirthError) {
        otherError.dateOfBirth = dateOfBirthError
      }
      if (isNaN(Number(day)) || isNaN(Number(year))) {
        otherError.dob = true;
      } else if (day && month && year) {
        const ageError = getApplicantsAge(other.dateOfBirth, dates) < 18 || Number(year) < 1900 || Number(day) > 31 || dobValidator(day, month, year);
        if (ageError) {
          otherError.ageError = ageError
        }
      }


      // if (other.dateOfBirth || other.birthYear) {
      //   const dobObject = new Date(other.dateOfBirth);
      //   const year = dobObject.getFullYear();
      //   const birthYear = other.birthYear;
      //   const ageError =
      //     getApplicantsAge(other.dateOfBirth, dates) < 18 ||
      //     Number(birthYear) < 1900;
      //   if (ageError) {
      //     otherError.ageError = ageError;
      //   }
      //   if (!dobValidCharacter) {
      //     otherError.dob = true;
      //   }
      // }

      if (Object.keys(otherError).length) {
        formErrors[other.localId] = otherError;
      }
    });
    if (Object.keys(formErrors).length) {
      setOtherAdultsFormErrors(formErrors);
      return true;
    }
    return false;
  };
  const dobValidator = (day, month, year) => {
    let monthNum = Number(month);
    let dayNum = Number(day);
    let yearNum = Number(year);

    // Function to check if it's a leap year
    const isLeapYear = (year) => {
      return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    };

    // Check for invalid days in February
    if (monthNum === 2) {
      if (isLeapYear(yearNum)) {
        return dayNum > 29; // In a leap year, February can have at most 29 days
      } else {
        return dayNum > 28; // In a non-leap year, February can have at most 28 days
      }
    }

    // Months with 30 days
    const monthsWith30Days = [4, 6, 9, 11]; // April, June, September, November

    if (monthsWith30Days.includes(monthNum)) {
      return dayNum > 30; // If the day exceeds 30 in these months, it's invalid
    }

    // For all other months (January, March, May, July, August, October, December)
    return dayNum > 31; // If the day exceeds 31, it's invalid
  };


  const resetOtherAdultFormError = (errorKey, value, memberId) => {
    let formErrors = otherAdultsFormErrors || {};
    let memberError = formErrors?.[memberId];
    if (!memberError) {
      memberError = {};
    }

    const memberNewError = { ...memberError };
    if (value) {
      memberNewError[errorKey] = value;
    } else {
      delete memberNewError[errorKey];
    }

    setOtherAdultsFormErrors((prev) => {
      return {
        ...prev,
        [memberId]: memberNewError
      };
    });
  };

  const onNext = () => {
    // if (anotherAdultLiving && haveFormErrors()) {
    if (haveFormErrors()) {
      return;
    }

    // navigate(`/${i18n.language}${t("routes.consent_to_share")}`);
    if (isSummary === "true") {
      navigate(`/${i18n.language}${t("routes.review_summary")}`);
    } else {
      navigate(`/${i18n.language}${t("routes.heat_your_home")}`);
    }
  };

  const addAnotherAdult = () => {
    dispatch(addNewAdultAction());
  };

  const removeAdult = (localId) => {
    dispatch(removeAdultAction(localId));
  };

  const handleReduxCleanup = () => {
    if (userData?.otherAdultsInformation.length > 0) {
      userData?.otherAdultsInformation.map((x) => {
        dispatch(removeAdultAction(x.localId));
        dispatch(removeFiles('otherAdultAssessmentFiles', {}));
      })
    }
    dispatch(addNewAdultAction());
  }

  return (
    <div>
      <div className="container">
        <main>
          <article>
            <div className="row" style={{ paddingTop: "1em" }}></div>
            <BackAnchor backPath={"routes.personal_information"} onBackClick={handleReduxCleanup} />
            <div className="row">
              <div className="col-xs-12 col-sm-8">
                <h1>{t("other_adult_living_form.title")}</h1>
              </div>
            </div>

            {otherAdultsInformation?.map((otherAdult, i) => {
              return (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <div className="col-xs-10">
                      <button
                        className="btn-lg ns-btn-3rd"
                        onClick={() => {
                          removeAdult(otherAdult?.localId);
                        }}
                        value={false}
                        name={t("other_adult_living_form.remove")}
                      >
                        <span style={{ paddingRight: "15px" }}>{"-"}</span>
                        {t("other_adult_living_form.remove")}
                      </button>
                      {otherAdultsInformation?.length > 1 && <hr />}

                    </div>
                  )}
                  {/* {i !== otherAdultsInformation?.length && <hr />} */}
                  <OtherAdultFormComponent
                    key={i + otherAdult?.localId}
                    t={t}
                    i18n={i18n}
                    otherAdultInfo={otherAdult}
                    index={i}
                    localId={otherAdult?.localId}
                    getDate={getDate}
                    setIsError={setIsError}
                    formErrors={otherAdultsFormErrors[otherAdult?.localId]}
                    resetOtherAdultFormError={resetOtherAdultFormError}
                  />

                  {i > 0 && (
                    <AsssementNoticeView
                      t={t}
                      i18n={i18n}
                      otherAdultId={otherAdult.localId}
                    />
                  )}
                </React.Fragment>
              );
            })}

            {otherAdultsInformation?.length < MAX_OTHER_ADULTS_COUNT && (
              <div className="col-xs-10">
                {otherAdultsInformation?.length > 1 && <hr />}
                <button
                  className="btn-lg ns-btn-3rd"
                  onClick={addAnotherAdult}
                  value={false}
                  name={t("other_adult_living_form.addAnother")}
                  style={{ marginBottom: '2em' }}
                >
                  <span style={{ paddingRight: "15px" }}>{"+"}</span>
                  {t("other_adult_living_form.addAnother")}
                </button>
              </div>
            )}

            <ContinueCancelButtons continueClick={onNext} />
          </article>
        </main>
      </div>
    </div>
  );
};

const AsssementNoticeView = ({ t, i18n, otherAdultId }) => {
  const dispatch = useDispatch();

  const [filesLoading, setFilesLoading] = useState(false);
  const [fileError, setFileError] = useState("");
  const assesmentFiles = useSelector(
    (state) => state.app.otherAdultAssessmentFiles[otherAdultId]
  );

  const assessmentFiles = useSelector((state) => state.app.otherAdultAssessmentFiles);

  const datesData = useSelector((state) => state.datesReducer);
  let currentLanguageDates;
  if (datesData) {
    currentLanguageDates = datesData[i18n.language];
  }

  const taxFiledYear = currentLanguageDates
    ? currentLanguageDates?.taxFiled
    : "#YEAR";


  const createDownloadUrl = (base64Data, fileName) => {
    const [meta, data] = base64Data.split(',');
    const mime = meta.match(/:(.*?);/)[1];
    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mime });
    return URL.createObjectURL(blob);
  };

  const handleFileChange = (fileList) => {
    // 	if (fileList.length > 1) {
    //     setFileError(t("other_adult_living_form.file_single_upload_error"));  
    //   }
    const file = fileList[0];

    if (!file) {
      return;
    }

    if (fileError) {
      setFileError("");
    }

    setFilesLoading(true);

    fileToBase64(file)
      .then((rawData) => {
        const data = rawData.split(",")[1];
        if (data && file) {
          const fileData = {
            changeType: "SAVE",
            data: data,
            name: file.name,
            size: bytesToSize(file.size),
            type: getFileTypeFromMimeType(file.type),
            url: createDownloadUrl(rawData, file.name)
          };
          dispatch(setOtherAdultAssessmentFiles({ [otherAdultId]: [fileData] }));
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setFilesLoading(false);
      });
  };

  const handleFileDelete = (fileName) => {
    const updatedFiles = (assessmentFiles[otherAdultId] || []).filter(file => file.name !== fileName);
    dispatch(setOtherAdultAssessmentFiles({ [otherAdultId]: updatedFiles }));
  };


  return (
    <div className="row">
      <div
        style={{
          fontSize: "1.8rem",
          marginTop: "20px",
          marginBottom: "20px",
          clear: "both"
        }}
      >
        {t("other_adult_living_form.notice_of_assessment")}
      </div>
      <p>
        {t("other_adult_living_form.assessment_desc").replace(
          "#YEAR",
          taxFiledYear
        )}
      </p>
      <p>{t("other_adult_living_form.assessment_desc2")}</p>
      <div>
        <b>{t("other_adult_living_form.upload_notice_label")}</b>
      </div>
      <p>{t("other_adult_living_form.upload_notice_helper")}</p>
      <div style={{ display: "flex", alignItems: "center" }}>
        <FileUpload handleFileChange={handleFileChange} />
        <Loader isLoading={filesLoading} />
      </div>
      {fileError && (
        <span id="file-count-error" className={"help-block-focused"}>
          <i
            aria-label={t("shared.error")}
            className="fa fa-exclamation-circle error-indicator errorAlert"
            aria-hidden="true"
          />
          <span className="ns-visually-hidden">{t("shared.error")}</span>
          {t(fileError)}
        </span>
      )}
      <div style={{ fontSize: ".8rem" }}>
        {t("other_adult_living_form.upload_helper_text1")}
      </div>
      <div style={{ fontSize: ".8rem" }}>
        {t("other_adult_living_form.upload_helper_text2")}
      </div>
      <div style={{ marginTop: "20px" }}>
        <b>{t("other_adult_living_form.uploaded_assessment_lable")}</b>
      </div>
      <ul>
        {assesmentFiles?.map((file) => {
          return <li key={file.name} style={{ marginBottom: "10px" }}>
            <span style={{ color: "#24569f", textDecoration: "underline" }}>
              <a
                href={file.url}
                download={file.name}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#24569f", textDecoration: "underline" }}>
                {file.name}
              </a>
            </span>
            <span style={{ color: "#24569f", marginLeft: "14px" }}>  {file.size}</span>
            <button
              onClick={() => handleFileDelete(file.name)}
              style={{ marginLeft: "10px", border: "none", background: "none", cursor: "pointer" }}
            >
              <FontAwesomeIcon icon={faTrash} alt="Delete" style={{ width: "16px", height: "14px", marginRight: "7px", color: "24569f" }} />
              <span style={{ color: "#24569f" }}>{t("upload_bill.delete")}</span>
            </button>
          </li>

        })}
      </ul>
    </div>
  );
};

export default withTranslation("common")(OtherAdultsFormView);
